import { config } from '../config';
import { db, resetAll } from '../data/harnessDb';
import type { BulkBlobRow } from '../data/harnessDb';
import { deriveStatus } from '../domain/status';
import { StorageError } from '../app/errors';
import { log } from '../app/logger';
import type {
  DiscoveryBucket,
  HypothesisRow,
  ObservationRow,
  SeedProfile,
} from '../types';

/**
 * Seeds the harness database to a realistic size before measurement.
 *
 * **Why this is not optional.** An IndexedDB commit into an empty object store is not the
 * transaction the product performs. Index maintenance cost grows with the index, and A-6 puts a
 * season at ~40 matches × 100–600 increments — tens of thousands of rows in the two stores the tap
 * transaction spans. Measuring against an empty database would produce a comfortable number and
 * leave A-1 exactly as unvalidated as it is today, which is the one outcome this unit cannot
 * afford. (`empirical-absence-not-invariant`: fast on an empty store is not evidence of fast.)
 *
 * Seeding is chunked so a large seed never holds a single transaction open for minutes — WebKit
 * will abort a long-running transaction, and an aborted seed that looked like it worked would
 * silently produce an under-sized database.
 */

const MODULE = 'seed';

const BUCKETS: readonly DiscoveryBucket[] = [
  'inPossession',
  'outOfPossession',
  'attTransition',
  'defTransition',
];

export interface SeedRequest {
  matches: number;
  observationsPerMatch: number;
  /** Bytes of synthetic audio to write into the bulk store. 0 skips it. */
  bulkAudioBytes: number;
  onProgress?: (fraction: number, label: string) => void;
}

export const SEED_PRESETS: Record<string, Omit<SeedRequest, 'onProgress'>> = {
  empty: { matches: 0, observationsPerMatch: 0, bulkAudioBytes: 0 },
  typical: {
    matches: config.SEED_MATCHES_DEFAULT,
    observationsPerMatch: config.SEED_OBSERVATIONS_PER_MATCH_DEFAULT,
    bulkAudioBytes: 0,
  },
  fullSeason: { matches: 40, observationsPerMatch: 600, bulkAudioBytes: 0 },
};

/** Deterministic ids, so a seeded database is reproducible and a re-seed is idempotent. */
function hypothesisId(match: number, index: number): string {
  return `h-${String(match).padStart(3, '0')}-${String(index).padStart(3, '0')}`;
}

function makeHypothesis(match: number, index: number, count: number): HypothesisRow {
  const bucket = BUCKETS[index % BUCKETS.length] as DiscoveryBucket;
  return {
    id: hypothesisId(match, index),
    matchId: `m-${String(match).padStart(3, '0')}`,
    text: `Seeded hypothesis ${match}/${index} — synthetic row for the U-0 latency harness`,
    phase: bucket,
    discoveryBucket: bucket,
    playerId: null,
    statusOverride: null,
    cachedCount: count,
    lastIncrementTs: count > 0 ? Date.now() : null,
    status: deriveStatus(null, count),
    // INV-5: only the first BOARD_CAP hypotheses per match hold a position. The compound index is
    // `&[matchId+boardPosition]`, and IndexedDB omits entries with a null component — so the
    // list-only tail is correctly unconstrained. Do not "fix" this.
    boardPosition: index < config.BOARD_CAP ? index : null,
    sourceTemplateId: null,
    carriedFromMatchId: null,
    carriedStatus: null,
    carriedCount: null,
  };
}

function makeObservation(match: number, seq: number, hypIndex: number): ObservationRow {
  return {
    id: `o-${String(match).padStart(3, '0')}-${String(seq).padStart(5, '0')}`,
    hypothesisId: hypothesisId(match, hypIndex),
    matchId: `m-${String(match).padStart(3, '0')}`,
    matchMinute: seq % 90,
    kind: 'increment',
    audioBlobId: null,
    createdAt: Date.now() - (seq * 1000),
  };
}

async function writeChunk<T>(table: { bulkPut: (rows: T[]) => Promise<unknown> }, rows: T[]): Promise<void> {
  try {
    await table.bulkPut(rows);
  } catch (cause) {
    throw new StorageError('Seeding failed while writing a chunk.', {
      cause,
      context: { rows: rows.length },
    });
  }
}

/**
 * @throws {StorageError} if any chunk fails. A partially seeded database is worse than none —
 *   the caller wipes and retries rather than measuring against an unknown row count.
 */
export async function seedDatabase(request: SeedRequest): Promise<SeedProfile> {
  const { matches, observationsPerMatch, bulkAudioBytes, onProgress } = request;
  const handle = db();

  log.info(MODULE, 'seeding harness database', { matches, observationsPerMatch, bulkAudioBytes });
  await resetAll();

  const hypothesesPerMatch = config.SEED_HYPOTHESES_PER_MATCH;
  const totalUnits = Math.max(1, matches);

  for (let m = 0; m < matches; m++) {
    const hypRows: HypothesisRow[] = [];
    for (let h = 0; h < hypothesesPerMatch; h++) {
      // Spread counts across the PATTERN_THRESHOLD boundary so both `tracking` and `confirmed`
      // rows exist — the `[matchId+statusOverride]` and `[matchId+cachedCount]` indexes then hold
      // a realistic spread of values rather than one repeated key.
      hypRows.push(makeHypothesis(m, h, h % 9));
    }
    await writeChunk(handle.hypotheses, hypRows);

    let obsBuffer: ObservationRow[] = [];
    for (let o = 0; o < observationsPerMatch; o++) {
      obsBuffer.push(makeObservation(m, o, o % hypothesesPerMatch));
      if (obsBuffer.length >= config.SEED_CHUNK_ROWS) {
        await writeChunk(handle.observations, obsBuffer);
        obsBuffer = [];
      }
    }
    if (obsBuffer.length > 0) await writeChunk(handle.observations, obsBuffer);

    onProgress?.((m + 1) / totalUnits, `Seeded match ${m + 1} of ${matches}`);
  }

  let bulkWritten = 0;
  if (bulkAudioBytes > 0) {
    const perBlob = config.SEED_AUDIO_BLOB_BYTES;
    const blobCount = Math.ceil(bulkAudioBytes / perBlob);
    const payload = new Uint8Array(perBlob);
    // Incompressible-ish content: a zero-filled blob can be stored far more cheaply than real
    // audio by a compressing storage layer, which would understate `storage.estimate()`.
    for (let i = 0; i < perBlob; i++) payload[i] = (i * 2654435761) % 251;

    for (let i = 0; i < blobCount; i++) {
      const row: BulkBlobRow = {
        id: `bulk-${String(i).padStart(5, '0')}`,
        bytes: perBlob,
        blob: new Blob([payload], { type: 'audio/mp4' }),
      };
      await writeChunk(handle.bulkBlobs, [row]);
      bulkWritten += perBlob;
      if (i % 20 === 0) onProgress?.(i / blobCount, `Seeded ${i} of ${blobCount} audio blobs`);
    }
  }

  const profile: SeedProfile = {
    matches,
    observationsPerMatch,
    hypothesesPerMatch,
    observationRows: await handle.observations.count(),
    hypothesisRows: await handle.hypotheses.count(),
    bulkBlobBytes: bulkWritten,
  };

  log.info(MODULE, 'seed complete', { ...profile });
  return profile;
}

/** Reads the current seed shape without re-seeding — used when resuming an existing database. */
export async function currentSeedProfile(): Promise<SeedProfile> {
  const handle = db();
  const [observationRows, hypothesisRows, blobs] = await Promise.all([
    handle.observations.count(),
    handle.hypotheses.count(),
    handle.bulkBlobs.toArray(),
  ]);
  return {
    matches: hypothesisRows > 0 ? Math.round(hypothesisRows / config.SEED_HYPOTHESES_PER_MATCH) : 0,
    observationsPerMatch:
      hypothesisRows > 0
        ? Math.round(observationRows / Math.max(1, hypothesisRows / config.SEED_HYPOTHESES_PER_MATCH))
        : 0,
    hypothesesPerMatch: config.SEED_HYPOTHESES_PER_MATCH,
    observationRows,
    hypothesisRows,
    bulkBlobBytes: blobs.reduce((sum, b) => sum + b.bytes, 0),
  };
}

/** Picks a hypothesis id that exists, for the runner to increment. */
export async function pickTargetHypothesisId(): Promise<string | null> {
  const handle = db();
  const row = await handle.hypotheses.limit(1).first();
  return row?.id ?? null;
}
